import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Group, Checkbox, Stack, Badge, Text } from 'components'

interface ProjectItem {
  id: string
  name: string
  original_codes?: string
  originalCodes?: string
  cycles?: Array<{ id?: string; name: string | Array<{ name?: string }> }>
  breadcrumb?: string
  lv1_name?: string
  lv1Name?: string
  color?: string
}

interface ItemCardProps {
  item: ProjectItem
  type: 'content' | 'skill'
  isSelected: boolean
  isAlreadyAdded: boolean
  onToggle: (itemId: string) => void
}

const getMantineColor = (hexColor: string): string => {
  // Normalizar para maiúsculas para comparação
  const normalizedColor = hexColor?.toUpperCase() || ''
  
  const colorMap: { [key: string]: string } = {
    '#FF9900': 'orange',
    '#00A86B': 'teal',
    '#FF6B6B': 'red',
    '#4DABF7': 'blue',
    '#69DB7C': 'green',
    '#FFD43B': 'yellow',
    '#9775FA': 'violet',
    '#F783AC': 'pink',
    '#6C757D': 'gray',
    '#868E96': 'gray'
  }
  
  return colorMap[normalizedColor] || 'gray'
}

export const ItemCard = memo(function ItemCard({ 
  item, 
  type, 
  isSelected, 
  isAlreadyAdded, 
  onToggle 
}: ItemCardProps) {
  const { t } = useTranslation()
  
  // Proteções robustas para evitar tela branca
  if (!item || !item.id || !item.name) {
    return null
  }
  
  const itemCode = item.original_codes || item.originalCodes || ''
  const itemName = item.lv1_name || item.lv1Name || ''
  const itemColor = item.color || (type === 'skill' ? '#00A86B' : '#FF9900')
  const mantineColor = getMantineColor(itemColor)
  
  return (
    <Card 
      key={item.id} 
      p='sm' 
      withBorder
      style={{ 
        opacity: isAlreadyAdded ? 0.6 : 1,
        backgroundColor: isSelected && !isAlreadyAdded ? 
          `${itemColor}15` : undefined,
        borderColor: isSelected && !isAlreadyAdded ? itemColor : undefined
      }}
    >
      <Group position='apart' align='flex-start'>
        <Checkbox
          checked={!!isAlreadyAdded || isSelected}
          disabled={!!isAlreadyAdded}
          onChange={() => onToggle(item.id)}
          style={{ alignSelf: 'flex-start' }}
        />
        <Stack spacing={4} style={{ flex: 1 }}>
          <Group spacing='xs'>
            <Badge 
              size='sm' 
              variant='filled' 
              color={mantineColor}
            >
              {type === 'skill' ? t('new_project_page.skill') : t('new_project_page.content')}{itemName && ` - ${itemName}`}
            </Badge>
            {itemCode && (
              <Badge size='sm' variant='outline' color='gray'>
                {itemCode}
              </Badge>
            )}
            {itemName && (
              <Text size='xs' color='dimmed'>
                {itemName}
              </Text>
            )}
          </Group>
          
          <Text size='sm' weight={500}>
            {item.name}
          </Text>
          
          {item.breadcrumb && (
            <Text size='xs' color='dimmed'>
              {item.breadcrumb}
            </Text>
          )}
          
          {item.cycles && item.cycles.length > 0 && (
            <Group spacing={4}>
              {(item.cycles || []).map((cycle, index) => {
                // Garantir que cycle existe e seja válido
                if (!cycle) return null
                
                // Garantir que cycle.name seja sempre string
                let cycleName = ''
                try {
                  if (typeof cycle.name === 'string') {
                    cycleName = cycle.name
                  } else if (cycle.name && Array.isArray(cycle.name) && cycle.name[0]) {
                    cycleName = cycle.name[0].name || ''
                  } else if (cycle.name && typeof cycle.name === 'object') {
                    cycleName = String(cycle.name)
                  }
                } catch (e) {
                  cycleName = ''
                }
                
                return cycleName ? (
                  <Badge key={cycle.id || index} size='xs' variant='dot' color='blue'>
                    {cycleName}
                  </Badge>
                ) : null
              }).filter(Boolean)}
            </Group>
          )}
          
          {isAlreadyAdded && (
            <Badge size='sm' color='gray' variant='filled'>
              {t('new_project_page.already_added')}
            </Badge>
          )}
        </Stack>
      </Group>
    </Card>
  )
})